import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, 
  Clock, 
  Phone, 
  Globe, 
  Star, 
  Users, 
  Car, 
  Utensils,
  ArrowLeft,
  Camera,
  Bath,
  Building,
  UserPlus,
  MessageCircle,
  Heart,
  Share2,
  Video
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { SocialActions } from '@/components/profile/SocialActions';
import { VenueUpcomingGames } from '@/components/venue/VenueUpcomingGames';
import { VenueUpcomingEvents } from '@/components/venue/VenueUpcomingEvents';
import { VenueGameSubmissionForm } from '@/components/venue/VenueGameSubmissionForm';
import { VenueEventSubmissionForm } from '@/components/venue/VenueEventSubmissionForm';
import VenueSectionsDisplay from '@/components/venue/VenueSectionsDisplay';
import VenueSectionsPricing from '@/components/venue/VenueSectionsPricing';
import { VenueRatingSystem } from '@/components/venue/VenueRatingSystem';
import { VenueRatingDisplay } from '@/components/venue/VenueRatingSystem';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { photoUploadService } from '@/services/photoUploadService';
import PhotoGallery from '@/components/profile/PhotoGallery';
import VideoPlayer from '@/components/media/VideoPlayer';
import { FeaturedSpotlight } from '@/components/featured/FeaturedSpotlight';
import { SocialMediaEmbeds } from '@/components/social/SocialMediaEmbeds';

interface VenueData {
  id: string;
  venue_name: string;
  venue_address: string;
  venue_description?: string;
  operating_hours?: string;
  phone?: string;
  website?: string;
  venue_capacity?: number;
  venue_type?: string;
  facilities_available?: string[];
  parking_available?: boolean;
  restrooms_available?: boolean;
  locker_rooms_available?: boolean;
  concessions_available?: boolean;
  hourly_rates?: number;
  venue_manager_name?: string;
  venue_manager_contact?: string;
  photo_gallery_links?: string[];
  highlight_video_links?: string[];
  profile_photo_url?: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  source: 'venues' | 'internal';
}

export default function VenueProfile() {
  const { venueSlug } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<VenueData | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState<{ average: number; count: number } | null>(null);
  const isMobile = useIsMobile();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  
  const currentUser = supabaseUser || internalUser;
  const isOwner = currentUser && venue && currentUser.id === venue.id;

  useEffect(() => {
    if (venueSlug) {
      loadVenueData();
    }
  }, [venueSlug]);

  useEffect(() => {
    if (venue?.profile_photo_url) {
      photoUploadService.getProfilePhotoUrl(venue.profile_photo_url)
        .then(url => setProfilePhotoUrl(url))
        .catch(error => console.error('Error loading profile photo:', error));
    }
  }, [venue?.profile_photo_url]);

  const loadVenueData = async () => {
    try {
      setLoading(true);
      console.log('🏢 Loading venue profile for:', venueSlug);

      let venueData: VenueData | null = null;

      // Check if it's an internal venue (by username/slug)
      if (!venueSlug?.startsWith('venue_')) {
        const { data: internalData, error: internalError } = await supabase
          .from('internal_user_profiles')
          .select(`
            *,
            internal_users!inner(
              id,
              username,
              role
            )
          `)
          .eq('internal_users.username', venueSlug)
          .eq('internal_users.role', 'venue')
          .single();

        if (internalData && !internalError) {
          venueData = {
            ...internalData,
            id: internalData.user_id,
            username: internalData.internal_users?.username,
            source: 'internal'
          } as VenueData;
        }
      }

      // If not found in internal, try venues table
      if (!venueData && venueSlug?.startsWith('venue_')) {
        const venueId = venueSlug.replace('venue_', '');
        const { data: venuesData, error: venuesError } = await supabase
          .from('venues')
          .select('*')
          .eq('venue_id', venueId)
          .single();

        if (venuesData && !venuesError) {
          venueData = {
            ...venuesData,
            id: venuesData.venue_id,
            venue_address: venuesData.venue_address || venuesData.address_line,
            source: 'venues'
          } as VenueData;
        }
      }

      if (!venueData) {
        toast.error('Venue not found');
        navigate('/venues/discover');
        return;
      }

      setVenue(venueData);
      
      // Load actual ratings data
      await loadVenueRatings(venueData.id);
    } catch (error) {
      console.error('Error loading venue data:', error);
      toast.error('Failed to load venue information');
      navigate('/venues/discover');
    } finally {
      setLoading(false);
    }
  };

  const loadVenueRatings = async (venueId: string) => {
    try {
      const { data, error } = await supabase
        .from('venue_customer_ratings')
        .select('rating')
        .eq('venue_id', venueId);

      if (error) {
        console.error('Error loading venue ratings:', error);
        return;
      }

      if (data && data.length > 0) {
        const average = data.reduce((sum, item) => sum + item.rating, 0) / data.length;
        setRatings({
          average: Math.round(average * 10) / 10,
          count: data.length
        });
      }
    } catch (error) {
      console.error('Error loading venue ratings:', error);
    }
  };

  const handleContact = () => {
    if (venue?.phone) {
      window.open(`tel:${venue.phone}`, '_self');
    } else if (venue?.venue_manager_contact) {
      window.open(`mailto:${venue.venue_manager_contact}`, '_self');
    } else {
      toast.info('Contact information not available');
    }
  };

  const handleWebsite = () => {
    if (venue?.website) {
      window.open(venue.website, '_blank');
    } else {
      toast.info('Website not available');
    }
  };

  // Convert Pexels webpage URLs to direct image URLs
  const convertPexelsUrl = (pexelsUrl: string): string => {
    if (pexelsUrl.includes('pexels.com/photo/')) {
      const match = pexelsUrl.match(/pexels\.com\/photo\/[^/]+-(\d+)/);
      if (match) {
        const photoId = match[1];
        return `https://images.pexels.com/photos/${photoId}/pexels-photo-${photoId}.jpeg?auto=compress&cs=tinysrgb&w=1200`;
      }
    }
    return pexelsUrl;
  };

  // Process photo links for display
  const processedPhotoLinks = venue?.photo_gallery_links?.map(convertPexelsUrl) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        <main className={`${isMobile ? 'pt-16 mb-16 py-4 px-4' : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'}`}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-muted-foreground">Loading venue...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-background">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        <main className={`${isMobile ? 'pt-16 mb-16 py-4 px-4' : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'}`}>
          <div className="max-w-4xl mx-auto text-center py-12">
            <h1 className="text-2xl font-bold text-foreground mb-4">Venue Not Found</h1>
            <Button onClick={() => navigate('/venues/discover')}>
              Back to Venue Discovery
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`${isMobile ? 'pt-16 mb-16 py-4 px-4' : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'}`}>
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Back Button */}
          <Button variant="outline" onClick={() => navigate('/venues/discover')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Venues
          </Button>

          {/* Hero Section */}
          <Card className="bg-gradient-to-r from-primary/20 to-secondary/20 border-primary/20 relative overflow-hidden">
            {profilePhotoUrl && (
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${profilePhotoUrl})`,
                }}
              >
                <div className="absolute inset-0 bg-black/70"></div>
              </div>
            )}
            <CardContent className="p-8 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                <div className="md:col-span-2">
                  <h1 className="text-4xl font-bold text-white mb-2">{venue.venue_name}</h1>
                  <div className="flex items-center gap-2 text-white/80 mb-4">
                    <MapPin className="h-5 w-5" />
                    <span>{venue.venue_address}</span>
                  </div>
                  {ratings && (
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                      <span className="font-semibold text-white">{ratings.average.toFixed(1)}</span>
                      <span className="text-white/80">({ratings.count} reviews)</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={handleContact} className="bg-primary hover:bg-primary/90">
                      <Phone className="h-4 w-4 mr-2" />
                      Contact
                    </Button>
                    {venue.website && (
                      <Button variant="outline" onClick={handleWebsite} className="border-white/20 text-white hover:bg-white/10">
                        <Globe className="h-4 w-4 mr-2" />
                        Website
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="text-center">
                  {profilePhotoUrl ? (
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-white/20">
                      <img 
                        src={profilePhotoUrl}
                        alt={venue.venue_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-4xl font-bold">
                      {venue.venue_name.charAt(0)}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Actions - Only show if not the owner */}
          {!isOwner && venue && (
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <SocialActions 
                  profile={{
                    id: venue.id,
                    username: venue.username || venue.venue_name.toLowerCase().replace(/\s+/g, ''),
                    first_name: venue.venue_name.split(' ')[0] || venue.venue_name,
                    last_name: venue.venue_name.split(' ').slice(1).join(' ') || '',
                    role: 'venue',
                    isInternal: venue.source === 'internal'
                  } as any}
                />
              </CardContent>
            </Card>
          )}

          {/* Featured Spotlight */}
          <FeaturedSpotlight 
            itemType="venue" 
            itemId={venue.id} 
            showPurchase={isOwner}
          />

          {/* Social Media Embeds */}
          <SocialMediaEmbeds 
            userId={venue.id}
            userType="venue"
            editable={isOwner}
          />

          {/* Tabs */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="facilities">Facilities</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="photos">Gallery</TabsTrigger>
              <TabsTrigger value="reviews">Reviews</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      Venue Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {venue.venue_description && (
                      <div>
                        <h4 className="font-semibold mb-2">Description</h4>
                        <p className="text-muted-foreground">{venue.venue_description}</p>
                      </div>
                    )}
                    
                    {venue.venue_type && (
                      <div>
                        <h4 className="font-semibold mb-2">Venue Type</h4>
                        <Badge variant="secondary">{venue.venue_type}</Badge>
                      </div>
                    )}
                    
                    {venue.venue_capacity && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span className="font-semibold">Capacity:</span>
                        <span>{venue.venue_capacity} people</span>
                      </div>
                    )}
                    
                    {venue.operating_hours && (
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 mt-1" />
                        <div>
                          <span className="font-semibold">Operating Hours:</span>
                          <p className="text-muted-foreground whitespace-pre-line">{venue.operating_hours}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Contact & Management</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {venue.venue_manager_name && (
                      <div>
                        <h4 className="font-semibold mb-2">Venue Manager</h4>
                        <p className="text-muted-foreground">{venue.venue_manager_name}</p>
                        {venue.venue_manager_contact && (
                          <p className="text-sm text-muted-foreground">{venue.venue_manager_contact}</p>
                        )}
                      </div>
                    )}
                    
                    {venue.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span className="font-semibold">Phone:</span>
                        <span>{venue.phone}</span>
                      </div>
                    )}
                    
                    {venue.source === 'internal' && (venue.first_name || venue.username) && (
                      <div>
                        <h4 className="font-semibold mb-2">Operated by</h4>
                        <p className="text-muted-foreground">
                          {venue.first_name && venue.last_name 
                            ? `${venue.first_name} ${venue.last_name}` 
                            : venue.username}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Upcoming Games and Events */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <VenueUpcomingGames venueId={venue.id} />
                <VenueUpcomingEvents venueId={venue.id} />
              </div>
            </TabsContent>

            <TabsContent value="facilities" className="space-y-4">
              {/* Venue Sections */}
              <VenueSectionsDisplay venueId={venue.id} />
              
              {/* Available Facilities */}
              <Card>
                <CardHeader>
                  <CardTitle>Available Facilities</CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.facilities_available && venue.facilities_available.length > 0 ? (
                    <div className="flex flex-wrap gap-3 mb-6">
                      {venue.facilities_available.map((facility, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="bg-primary/10 text-primary border-primary/20 px-3 py-1"
                        >
                          {facility}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground mb-6">No specific facilities listed</p>
                  )}
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                      venue.parking_available 
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' 
                        : 'bg-muted border-border'
                    }`}>
                      <Car className={`h-4 w-4 ${venue.parking_available ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${venue.parking_available ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                        Parking
                      </span>
                    </div>
                    
                    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                      venue.restrooms_available 
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' 
                        : 'bg-muted border-border'
                    }`}>
                      <Bath className={`h-4 w-4 ${venue.restrooms_available ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${venue.restrooms_available ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                        Restrooms
                      </span>
                    </div>
                    
                    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                      venue.locker_rooms_available 
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' 
                        : 'bg-muted border-border'
                    }`}>
                      <Building className={`h-4 w-4 ${venue.locker_rooms_available ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${venue.locker_rooms_available ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                        Locker Rooms
                      </span>
                    </div>
                    
                    <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                      venue.concessions_available 
                        ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' 
                        : 'bg-muted border-border'
                    }`}>
                      <Utensils className={`h-4 w-4 ${venue.concessions_available ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                      <span className={`text-sm ${venue.concessions_available ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'}`}>
                        Concessions
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Pricing Information</CardTitle>
                </CardHeader>
                <CardContent>
                  {venue.hourly_rates && (
                    <div className="text-center py-4 border-b mb-6">
                      <div className="text-4xl font-bold text-primary mb-2">${venue.hourly_rates}</div>
                      <div className="text-muted-foreground">Base hourly rate</div>
                    </div>
                  )}
                  
                  {/* Section-Specific Pricing */}
                  <VenueSectionsPricing venueId={venue.id} />
                  
                  {!venue.hourly_rates && (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">Pricing information not available</p>
                      <p className="text-sm text-muted-foreground mt-2">Contact venue for rates</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="photos" className="space-y-6">
              {/* Photos Section */}
              {processedPhotoLinks.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-foreground">Photo Gallery</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {processedPhotoLinks.map((photoUrl, index) => (
                      <div key={index} className="aspect-video bg-muted rounded-lg overflow-hidden">
                        <img 
                          src={photoUrl}
                          alt={`${venue.venue_name} photo ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div 
                          className="w-full h-full hidden items-center justify-center bg-muted/50 text-muted-foreground"
                          style={{ display: 'none' }}
                        >
                          <div className="text-center">
                            <p className="text-sm">Unable to load image</p>
                            <a 
                              href={venue.photo_gallery_links?.[index] || photoUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline"
                            >
                              View original
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              
              {/* Videos Section */}
              {venue.highlight_video_links && venue.highlight_video_links.length > 0 && (
                <div className="space-y-4 mt-8">
                  <h3 className="text-xl font-semibold text-foreground">Video Gallery</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {venue.highlight_video_links.map((videoUrl, index) => (
                      <VideoPlayer 
                        key={index} 
                        url={videoUrl} 
                        title={`${venue.venue_name} Video ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empty State */}
              {(!processedPhotoLinks.length && (!venue.highlight_video_links || !venue.highlight_video_links.length)) && (
                <Card className="bg-card border-border">
                  <CardContent className="text-center py-12">
                    <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">No Media Available</h3>
                    <p className="text-muted-foreground">This venue hasn't uploaded any photos or videos yet.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Display Ratings */}
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Reviews</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VenueRatingDisplay venueId={venue.id} showReviews={true} />
                  </CardContent>
                </Card>

                {/* Submit Rating */}
                {currentUser && currentUser.id !== venue.id && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Leave a Review</CardTitle>
                    </CardHeader>
                    <CardContent>
                    <VenueRatingSystem 
                      venueId={venue.id} 
                      ratingType="event_experience"
                      onRatingSubmitted={() => {
                        toast.success('Rating submitted successfully!');
                        loadVenueRatings(venue.id);
                      }}
                    />
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="services" className="space-y-6">
              {/* Submission Forms */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Submit Game Request</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VenueGameSubmissionForm venueId={venue.id} onSuccess={() => {
                      toast.success('Game request submitted successfully!');
                    }} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Submit Event Request</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VenueEventSubmissionForm venueId={venue.id} onSuccess={() => {
                      toast.success('Event request submitted successfully!');
                    }} />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
}